// frontend/src/facturas/components/billing/BillingSection.js
// ═══════════════════════════════════════════════════════════════════════════
// CENTRO DE FACTURACION Y SUSCRIPCION - UrbanQ
// Diseno basado en AdminContabilidad.js y AdminSupport.js
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './BillingSection.css';
import '../../../styles/AdminTabs.css';
import {
    FiCreditCard, FiFileText, FiDollarSign, FiSettings,
    FiClock, FiCheck, FiAlertCircle, FiX,
    FiRefreshCw, FiDownload, FiCalendar, FiChevronRight,
    FiCheckCircle, FiAlertTriangle, FiInfo, FiExternalLink,
    FiTrendingUp, FiActivity, FiPieChart, FiEye
} from 'react-icons/fi';
import { FaPaypal } from 'react-icons/fa';
import { API_ENDPOINTS } from '../../../config/api';
import { getPlansArray, getPlanById } from '../../../config/planConfig';

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const BillingSection = ({ user, onPaymentSuccess }) => {
    const navigate = useNavigate();
    const plans = getPlansArray();

    // Estados
    const [activeTab, setActiveTab] = useState('resumen');
    const [loading, setLoading] = useState(true);
    const [billingInfo, setBillingInfo] = useState(null);
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [notification, setNotification] = useState(null);

    // Estados de renovacion
    const [renewalSuccess, setRenewalSuccess] = useState(false);

    // Obtener usuario actual
    const currentUser = user || JSON.parse(localStorage.getItem('user') || '{}');
    const token = localStorage.getItem('token');

    // ═══════════════════════════════════════════════════════════════════════
    // FUNCIONES AUXILIARES
    // ═══════════════════════════════════════════════════════════════════════

    const formatCurrency = (amount) => {
        if (!amount) return '$0.00';
        return `$${parseFloat(amount).toFixed(2)}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('es-EC', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // NOTIFICACIONES
    // ═══════════════════════════════════════════════════════════════════════

    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 5000);
    };

    // ═══════════════════════════════════════════════════════════════════════
    // FUNCIONES DE API
    // ═══════════════════════════════════════════════════════════════════════

    const fetchBillingData = useCallback(async () => {
        setLoading(true);
        try {
            // Obtener informacion de facturacion
            const infoResponse = await fetch(API_ENDPOINTS.billingInfo, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (infoResponse.ok) {
                const infoData = await infoResponse.json();
                setBillingInfo(infoData.data);
            }

            // Obtener historial de pagos
            const historyResponse = await fetch(`${API_ENDPOINTS.billingHistory}?limit=50`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (historyResponse.ok) {
                const historyData = await historyResponse.json();
                setPaymentHistory(historyData.payments || []);
            }
        } catch (error) {
            console.error('Error fetching billing data:', error);
            showNotification('Error al cargar informacion de facturacion', 'error');
        } finally {
            setLoading(false);
        }
    }, [token]);

    // Cargar datos iniciales
    useEffect(() => {
        fetchBillingData();
    }, [fetchBillingData]);

    // ═══════════════════════════════════════════════════════════════════════
    // FUNCIONES DE RENOVACION
    // ═══════════════════════════════════════════════════════════════════════

    const shouldShowRenewal = useCallback(() => {
        if (!billingInfo?.subscription) return false;
        const { days_remaining, is_active, is_trial } = billingInfo.subscription;
        return days_remaining <= 7 || !is_active || is_trial;
    }, [billingInfo]);

    const handleRenewalSuccess = (paymentDetails) => {
        console.log('Renovacion exitosa:', paymentDetails);
        setRenewalSuccess(true);
        fetchBillingData();
        showNotification('Renovacion completada exitosamente', 'success');
        if (onPaymentSuccess) {
            onPaymentSuccess(paymentDetails);
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // ACCIONES
    // ═══════════════════════════════════════════════════════════════════════

    const handleChangePlan = () => {
        window.location.href = '/planes';
    };

    const handleCancelSubscription = async () => {
        if (!window.confirm('¿Estas seguro que deseas cancelar tu suscripcion? Esta accion no se puede deshacer.')) {
            return;
        }

        try {
            const response = await fetch(API_ENDPOINTS.billingCancel, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    reason: 'Usuario solicito cancelacion desde el dashboard'
                })
            });

            if (response.ok) {
                showNotification('Suscripcion cancelada exitosamente', 'success');
                fetchBillingData();
            } else {
                throw new Error('Error al cancelar suscripcion');
            }
        } catch (err) {
            console.error('Error:', err);
            showNotification('Error al cancelar suscripcion: ' + err.message, 'error');
        }
    };

    // Calcular metricas
    const subscription = billingInfo?.subscription;
    const summary = billingInfo?.summary;

    const calculateDaysRemaining = () => {
        if (!subscription?.next_billing_date) return 0;
        const nextDate = new Date(subscription.next_billing_date);
        const today = new Date();
        const diff = Math.ceil((nextDate - today) / (1000 * 60 * 60 * 24));
        return diff > 0 ? diff : 0;
    };

    // ═══════════════════════════════════════════════════════════════════════
    // RENDERIZADO
    // ═══════════════════════════════════════════════════════════════════════

    if (loading) {
        return (
            <div className="urbanq-billing-container">
                <div className="urbanq-billing-loading">
                    <FiRefreshCw className="spinning" />
                    <p>Cargando informacion de facturacion...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="urbanq-billing-container">
            {/* Notificacion */}
            {notification && (
                <div className={`urbanq-billing-notification ${notification.type}`}>
                    {notification.type === 'success' ? <FiCheck /> : <FiAlertCircle />}
                    <span>{notification.message}</span>
                    <button onClick={() => setNotification(null)}><FiX /></button>
                </div>
            )}

            {/* Tabs */}
            <div className="urbanq-admin-tabs">
                <button
                    className={`urbanq-admin-tab ${activeTab === 'resumen' ? 'active' : ''}`}
                    onClick={() => setActiveTab('resumen')}
                >
                    <FiPieChart /> Resumen
                </button>
                <button
                    className={`urbanq-admin-tab ${activeTab === 'historial' ? 'active' : ''}`}
                    onClick={() => setActiveTab('historial')}
                >
                    <FiFileText /> Historial de Pagos
                </button>
                <button
                    className={`urbanq-admin-tab ${activeTab === 'metodo' ? 'active' : ''}`}
                    onClick={() => setActiveTab('metodo')}
                >
                    <FaPaypal /> Metodo de Pago
                </button>
                <button
                    className={`urbanq-admin-tab ${activeTab === 'facturas' ? 'active' : ''}`}
                    onClick={() => setActiveTab('facturas')}
                >
                    <FiDownload /> Mis Facturas
                </button>
                <button
                    className={`urbanq-admin-tab ${activeTab === 'configuracion' ? 'active' : ''}`}
                    onClick={() => setActiveTab('configuracion')}
                >
                    <FiSettings /> Configuracion
                </button>
            </div>

            {/* Contenido */}
            <div className="urbanq-billing-content">
                {/* ═══════════════════════════════════════════════════════════════ */}
                {/* TAB: RESUMEN */}
                {/* ═══════════════════════════════════════════════════════════════ */}
                {activeTab === 'resumen' && (
                    <div className="urbanq-billing-resumen-tab">
                        {/* Metricas del Plan */}
                        <div className="urbanq-billing-section">
                            <div className="urbanq-billing-section-header">
                                <h2><FiActivity /> Informacion del Plan</h2>
                                <button
                                    className="urbanq-billing-refresh-btn"
                                    onClick={fetchBillingData}
                                    disabled={loading}
                                >
                                    <FiRefreshCw className={loading ? 'spinning' : ''} />
                                </button>
                            </div>

                            <div className="urbanq-billing-metrics-grid">
                                <MetricCard
                                    icon={<FiCreditCard />}
                                    label="Plan Actual"
                                    value={subscription?.plan_name || 'Sin Plan'}
                                    subtitle={subscription?.is_active ? 'Activo' : 'Inactivo'}
                                    color="purple"
                                />
                                <MetricCard
                                    icon={<FiDollarSign />}
                                    label="Monto Mensual"
                                    value={formatCurrency(subscription?.amount > 0 ? subscription.amount : getPlanById(subscription?.plan_type)?.price || 40)}
                                    subtitle="USD"
                                    color="green"
                                />
                                <MetricCard
                                    icon={<FiCalendar />}
                                    label="Proximo Cobro"
                                    value={formatDate(subscription?.next_billing_date)}
                                    subtitle=""
                                    color="blue"
                                />
                                <MetricCard
                                    icon={<FiClock />}
                                    label="Dias Restantes"
                                    value={subscription?.days_remaining || calculateDaysRemaining()}
                                    subtitle="dias"
                                    color="orange"
                                />
                            </div>
                        </div>

                        {/* Alerta de Renovacion */}
                        {shouldShowRenewal() && (
                            <div className="urbanq-billing-section urbanq-billing-renewal-section">
                                <div className="urbanq-billing-renewal-alert">
                                    <FiAlertTriangle className="alert-icon" />
                                    <div className="alert-content">
                                        {subscription?.days_remaining <= 0 ? (
                                            <span className="alert-danger">Tu suscripcion ha vencido</span>
                                        ) : subscription?.days_remaining <= 7 ? (
                                            <span className="alert-warning">Tu suscripcion vence en {subscription.days_remaining} dias</span>
                                        ) : subscription?.is_trial ? (
                                            <span className="alert-info">Tu periodo de prueba terminara pronto</span>
                                        ) : null}
                                    </div>
                                </div>

                                {renewalSuccess ? (
                                    <div className="urbanq-billing-renewal-success">
                                        <FiCheckCircle />
                                        <span>Renovacion exitosa! Tu suscripcion ha sido actualizada.</span>
                                    </div>
                                ) : (
                                    <div>
                                        <h4 style={{ color: '#e2e8f0', margin: '16px 0 12px', fontSize: '15px' }}>
                                            Selecciona un plan para renovar:
                                        </h4>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                                            {plans.map(plan => (
                                                <div
                                                    key={plan.id}
                                                    onClick={() => navigate('/billing/checkout', {
                                                        state: {
                                                            plan: { ...plan, isTrial: false, finalPrice: plan.price },
                                                            userId: currentUser.id,
                                                            condominiumId: currentUser.condominium_id,
                                                            isRenewal: true
                                                        }
                                                    })}
                                                    style={{
                                                        background: plan.recommended ? 'rgba(118, 75, 162, 0.15)' : 'rgba(255,255,255,0.04)',
                                                        border: plan.recommended ? '2px solid #764ba2' : '1px solid rgba(255,255,255,0.1)',
                                                        borderRadius: '12px',
                                                        padding: '16px',
                                                        cursor: 'pointer',
                                                        textAlign: 'center',
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(118,75,162,0.2)'; }}
                                                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                                                >
                                                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#e2e8f0' }}>{plan.name}</div>
                                                    <div style={{ fontSize: '24px', fontWeight: 700, color: plan.color, margin: '8px 0 4px' }}>${plan.price}</div>
                                                    <div style={{ fontSize: '12px', color: '#718096' }}>{plan.duration}</div>
                                                    {plan.badge && <div style={{ fontSize: '11px', color: '#764ba2', fontWeight: 600, marginTop: '6px' }}>{plan.badge}</div>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Resumen de Pagos */}
                        <div className="urbanq-billing-section">
                            <h2><FiTrendingUp /> Resumen de Pagos</h2>
                            <div className="urbanq-billing-summary-grid">
                                <SummaryCard
                                    icon={<FiDollarSign />}
                                    label="Pagado Este Mes"
                                    value={formatCurrency(summary?.total_this_month || 0)}
                                    color="green"
                                />
                                <SummaryCard
                                    icon={<FiTrendingUp />}
                                    label="Pagado Este Ano"
                                    value={formatCurrency(summary?.total_this_year || 0)}
                                    color="blue"
                                />
                                <SummaryCard
                                    icon={<FiClock />}
                                    label="Ultimo Pago"
                                    value={summary?.last_payment?.date || 'N/A'}
                                    subtitle={summary?.last_payment ? formatCurrency(summary.last_payment.amount) : ''}
                                    color="purple"
                                />
                            </div>
                        </div>

                        {/* Ultimos Pagos */}
                        <div className="urbanq-billing-section">
                            <div className="urbanq-billing-section-header">
                                <h2><FiClock /> Ultimos Pagos</h2>
                                <button
                                    className="urbanq-billing-btn secondary"
                                    onClick={() => setActiveTab('historial')}
                                >
                                    Ver todos <FiChevronRight />
                                </button>
                            </div>

                            <div className="urbanq-billing-recent-payments">
                                {paymentHistory.slice(0, 5).map((payment, index) => (
                                    <div key={payment.id || index} className="urbanq-billing-payment-item">
                                        <div className="urbanq-billing-payment-icon">
                                            <FiCheck />
                                        </div>
                                        <div className="urbanq-billing-payment-info">
                                            <span className="urbanq-billing-payment-desc">
                                                {payment.description || `Pago de ${payment.plan_type || 'suscripcion'}`}
                                            </span>
                                            <span className="urbanq-billing-payment-method">
                                                {payment.payment_method || 'PayPal'}
                                            </span>
                                        </div>
                                        <div className="urbanq-billing-payment-amount">
                                            {formatCurrency(payment.amount)}
                                        </div>
                                        <div className="urbanq-billing-payment-date">
                                            {formatDate(payment.payment_date)}
                                        </div>
                                    </div>
                                ))}
                                {paymentHistory.length === 0 && (
                                    <div className="urbanq-billing-no-data">
                                        <FiInfo />
                                        <p>No hay pagos registrados aun</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* ═══════════════════════════════════════════════════════════════ */}
                {/* TAB: HISTORIAL DE PAGOS */}
                {/* ═══════════════════════════════════════════════════════════════ */}
                {activeTab === 'historial' && (
                    <div className="urbanq-billing-historial-tab">
                        <div className="urbanq-billing-section">
                            <div className="urbanq-billing-section-header">
                                <h2><FiFileText /> Historial de Pagos ({paymentHistory.length})</h2>
                                <div className="urbanq-billing-total-badge">
                                    Total: {formatCurrency(summary?.total_this_year || 0)}
                                </div>
                            </div>

                            {paymentHistory.length > 0 ? (
                                <div className="urbanq-billing-table-container">
                                    <table className="urbanq-billing-table">
                                        <thead>
                                            <tr>
                                                <th>Fecha</th>
                                                <th>Descripcion</th>
                                                <th>Metodo</th>
                                                <th>Monto</th>
                                                <th>Estado</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paymentHistory.map((payment, index) => (
                                                <tr key={payment.id || index}>
                                                    <td>{formatDate(payment.payment_date)}</td>
                                                    <td>
                                                        <div className="payment-description">
                                                            <span className="desc-main">
                                                                {payment.description || `Pago de ${payment.plan_type || 'suscripcion'}`}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="payment-method-badge">
                                                            <FaPaypal /> {payment.payment_method || 'PayPal'}
                                                        </span>
                                                    </td>
                                                    <td className="payment-amount">{formatCurrency(payment.amount)}</td>
                                                    <td>
                                                        <span className={`urbanq-billing-status-badge ${payment.status}`}>
                                                            {payment.status === 'completed' && <FiCheck />}
                                                            {payment.status === 'pending' && <FiClock />}
                                                            {payment.status_label || payment.status || 'Completado'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="urbanq-billing-empty-state">
                                    <FiFileText className="urbanq-billing-empty-icon" />
                                    <h3>No hay historial de pagos</h3>
                                    <p>Los pagos realizados apareceran aqui</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ═══════════════════════════════════════════════════════════════ */}
                {/* TAB: METODO DE PAGO */}
                {/* ═══════════════════════════════════════════════════════════════ */}
                {activeTab === 'metodo' && (
                    <div className="urbanq-billing-metodo-tab">
                        <div className="urbanq-billing-section">
                            <h2><FaPaypal /> Metodo de Pago Configurado</h2>
                            <p className="urbanq-billing-section-desc">
                                Tu metodo de pago actual para la renovacion de suscripcion
                            </p>

                            <div className="urbanq-billing-payment-method-card configured">
                                <div className="urbanq-billing-pm-icon paypal">
                                    <FaPaypal />
                                </div>
                                <div className="urbanq-billing-pm-info">
                                    <h4>PayPal</h4>
                                    <p>Pagos seguros con tu cuenta PayPal</p>
                                    {summary?.last_payment && (
                                        <span className="urbanq-billing-pm-last-use">
                                            Ultimo uso: {formatDate(summary.last_payment.date)}
                                        </span>
                                    )}
                                </div>
                                <div className="urbanq-billing-pm-status active">
                                    <FiCheck /> Activo
                                </div>
                            </div>

                            <div className="urbanq-billing-config-hint">
                                <FiInfo />
                                <span>Los pagos se procesan de forma segura a traves de PayPal. No almacenamos informacion de tarjetas de credito.</span>
                            </div>
                        </div>

                        <div className="urbanq-billing-section">
                            <h2><FiCreditCard /> Informacion de Seguridad</h2>
                            <div className="urbanq-billing-security-info">
                                <div className="security-item">
                                    <FiCheck className="security-icon" />
                                    <div>
                                        <strong>Encriptacion SSL</strong>
                                        <p>Todas las transacciones estan protegidas con encriptacion de 256 bits</p>
                                    </div>
                                </div>
                                <div className="security-item">
                                    <FiCheck className="security-icon" />
                                    <div>
                                        <strong>PayPal Buyer Protection</strong>
                                        <p>Tus compras estan protegidas por las politicas de PayPal</p>
                                    </div>
                                </div>
                                <div className="security-item">
                                    <FiCheck className="security-icon" />
                                    <div>
                                        <strong>Sin almacenamiento de datos</strong>
                                        <p>No guardamos informacion sensible de pago en nuestros servidores</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ═══════════════════════════════════════════════════════════════ */}
                {/* TAB: MIS FACTURAS */}
                {/* ═══════════════════════════════════════════════════════════════ */}
                {activeTab === 'facturas' && (
                    <div className="urbanq-billing-facturas-tab">
                        <div className="urbanq-billing-section">
                            <div className="urbanq-billing-section-header">
                                <h2><FiDownload /> Facturas de Suscripcion</h2>
                            </div>

                            {paymentHistory.filter(p => p.status === 'completed').length > 0 ? (
                                <div className="urbanq-billing-table-container">
                                    <table className="urbanq-billing-table">
                                        <thead>
                                            <tr>
                                                <th>Numero</th>
                                                <th>Fecha</th>
                                                <th>Concepto</th>
                                                <th>Monto</th>
                                                <th>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paymentHistory
                                                .filter(p => p.status === 'completed')
                                                .map((payment, index) => (
                                                    <tr key={payment.id || index}>
                                                        <td className="invoice-number">
                                                            INV-{new Date().getFullYear()}-{String(index + 1).padStart(4, '0')}
                                                        </td>
                                                        <td>{formatDate(payment.payment_date)}</td>
                                                        <td>{payment.description || `Suscripcion ${payment.plan_type}`}</td>
                                                        <td className="payment-amount">{formatCurrency(payment.amount)}</td>
                                                        <td>
                                                            <div className="urbanq-billing-actions">
                                                                <button
                                                                    className="urbanq-billing-action-btn"
                                                                    onClick={() => viewInvoice(payment, index)}
                                                                    title="Ver factura"
                                                                >
                                                                    <FiEye />
                                                                </button>
                                                                <button
                                                                    className="urbanq-billing-action-btn"
                                                                    onClick={() => viewInvoice(payment, index)}
                                                                    title="Descargar"
                                                                >
                                                                    <FiDownload />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="urbanq-billing-empty-state">
                                    <FiFileText className="urbanq-billing-empty-icon" />
                                    <h3>No hay facturas disponibles</h3>
                                    <p>Las facturas se generan automaticamente al completar un pago</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ═══════════════════════════════════════════════════════════════ */}
                {/* TAB: CONFIGURACION */}
                {/* ═══════════════════════════════════════════════════════════════ */}
                {activeTab === 'configuracion' && (
                    <div className="urbanq-billing-config-tab">
                        <div className="urbanq-billing-section">
                            <h2><FiSettings /> Configuracion de Suscripcion</h2>
                            <p className="urbanq-billing-section-desc">
                                Administra tu plan actual y opciones de suscripcion
                            </p>

                            {/* Plan Actual */}
                            <div className="urbanq-billing-plan-card">
                                <div className="urbanq-billing-plan-header">
                                    <div className="plan-badge">
                                        <FiCreditCard />
                                        <span>Plan Actual</span>
                                    </div>
                                    <div className={`plan-status ${subscription?.is_active ? 'active' : 'inactive'}`}>
                                        {subscription?.is_active ? 'Activo' : 'Inactivo'}
                                    </div>
                                </div>
                                <div className="urbanq-billing-plan-body">
                                    <h3>{subscription?.plan_name || 'Sin Plan'}</h3>
                                    <div className="plan-price">
                                        <span className="price">{formatCurrency(subscription?.amount)}</span>
                                        <span className="period">/ mes</span>
                                    </div>
                                    <div className="plan-features">
                                        <div className="feature">
                                            <FiCheck /> Gestion completa de residentes
                                        </div>
                                        <div className="feature">
                                            <FiCheck /> Control de pagos y alicuotas
                                        </div>
                                        <div className="feature">
                                            <FiCheck /> Reportes y estadisticas
                                        </div>
                                        <div className="feature">
                                            <FiCheck /> Soporte tecnico prioritario
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Acciones */}
                            <div className="urbanq-billing-config-actions">
                                <button
                                    className="urbanq-billing-btn primary large"
                                    onClick={handleChangePlan}
                                >
                                    <FiExternalLink /> Cambiar Plan
                                </button>
                            </div>
                        </div>

                        {/* Cancelacion */}
                        <div className="urbanq-billing-section urbanq-billing-danger-zone">
                            <h2><FiAlertTriangle /> Zona de Peligro</h2>
                            <div className="urbanq-billing-danger-content">
                                <div className="danger-info">
                                    <h4>Cancelar Suscripcion</h4>
                                    <p>
                                        Al cancelar tu suscripcion perderas acceso a todas las funcionalidades
                                        al finalizar el periodo actual. Esta accion no se puede deshacer.
                                    </p>
                                </div>
                                <button
                                    className="urbanq-billing-btn danger"
                                    onClick={handleCancelSubscription}
                                >
                                    <FiX /> Cancelar Suscripcion
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// FUNCION PARA VER FACTURA
// ═══════════════════════════════════════════════════════════════════════════

const viewInvoice = (payment, index) => {
    const invoiceNumber = `INV-${new Date().getFullYear()}-${String(index + 1).padStart(4, '0')}`;
    const invoiceHTML = `
    <!DOCTYPE html>
    <html>
    <head>
        <title>Factura ${invoiceNumber}</title>
        <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; margin: 20px; background: #f8f9fa; }
            .factura { background: white; padding: 40px; border-radius: 16px; box-shadow: 0 10px 40px rgba(0,0,0,0.1); max-width: 600px; margin: 0 auto; }
            .header { text-align: center; border-bottom: 3px solid #667eea; padding-bottom: 25px; margin-bottom: 25px; }
            .header h1 { color: #667eea; margin: 0; font-size: 32px; }
            .header p { color: #718096; margin: 10px 0 0 0; }
            .info-row { display: flex; justify-content: space-between; margin: 15px 0; padding: 10px 0; border-bottom: 1px solid #e2e8f0; }
            .info-row:last-of-type { border-bottom: none; }
            .label { color: #718096; }
            .value { font-weight: 600; color: #2d3748; }
            .total { font-size: 28px; color: #48bb78; font-weight: bold; text-align: center; margin: 30px 0; padding: 20px; background: linear-gradient(135deg, #f0fff4 0%, #c6f6d5 100%); border-radius: 12px; }
            .footer { text-align: center; margin-top: 30px; color: #718096; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 14px; }
            .logo { font-size: 24px; font-weight: 800; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; margin-bottom: 10px; }
        </style>
    </head>
    <body>
        <div class="factura">
            <div class="header">
                <div class="logo">UrbanQ</div>
                <h1>FACTURA</h1>
                <p>N: ${invoiceNumber}</p>
            </div>
            <div class="info-row"><span class="label">Concepto:</span> <span class="value">${payment.description || 'Suscripcion UrbanQ'}</span></div>
            <div class="info-row"><span class="label">Fecha:</span> <span class="value">${new Date(payment.payment_date).toLocaleDateString('es-EC')}</span></div>
            <div class="info-row"><span class="label">Metodo de Pago:</span> <span class="value">${payment.payment_method || 'PayPal'}</span></div>
            <div class="info-row"><span class="label">Estado:</span> <span class="value">Pagado</span></div>
            <div class="total">Total: $${parseFloat(payment.amount).toFixed(2)} USD</div>
            <div class="footer">
                <p>Gracias por confiar en UrbanQ</p>
                <p>Generado el: ${new Date().toLocaleDateString('es-EC')}</p>
            </div>
        </div>
    </body>
    </html>`;

    const ventana = window.open('', '_blank');
    ventana.document.write(invoiceHTML);
    ventana.document.close();
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTES AUXILIARES
// ═══════════════════════════════════════════════════════════════════════════

function MetricCard({ icon, label, value, subtitle, color }) {
    return (
        <div className={`urbanq-billing-metric-card ${color}`}>
            <div className="urbanq-billing-metric-icon">
                {icon}
            </div>
            <div className="urbanq-billing-metric-content">
                <span className="urbanq-billing-metric-value">{value}</span>
                <span className="urbanq-billing-metric-label">{label}</span>
                {subtitle && <span className="urbanq-billing-metric-subtitle">{subtitle}</span>}
            </div>
        </div>
    );
}

function SummaryCard({ icon, label, value, subtitle, color }) {
    return (
        <div className={`urbanq-billing-summary-card ${color}`}>
            <div className="urbanq-billing-summary-icon">
                {icon}
            </div>
            <div className="urbanq-billing-summary-content">
                <span className="urbanq-billing-summary-label">{label}</span>
                <span className="urbanq-billing-summary-value">{value}</span>
                {subtitle && <span className="urbanq-billing-summary-subtitle">{subtitle}</span>}
            </div>
        </div>
    );
}

export default BillingSection;
